import React, { useMemo } from 'react';

import arrayMutators from 'final-form-arrays';
import { Form, Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Label from 'components/shared/FormAdapters/FormDefault/Label';
import LabelWrapper from 'components/shared/FormAdapters/FormDefault/Label/Wrapper';
import FormErrorText from 'components/shared/FormErrorText';
import Loader from 'components/shared/Loader';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import GoogleMaps from 'components/shared/SubForms/GoogleMaps';
import { countryIdMutator, coordsMutator, addressMutator } from 'components/shared/SubForms/GoogleMaps/mutators';
import FormTitle from 'components/shared/Typography/FormTitle';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import { pageDataSelector } from 'store/page/selectors';
import { getCoordsString, capitalizeAllWordsForInput } from 'utils/common';
import generateHandleValue from 'utils/form/mutators/generateHandleValue';

import { BuilderFooterButton } from '../../shared/BuilderFooterButton';
import { usePageContext } from '../../shared/context';
import PageFormButtons from '../../shared/PageFormButtons';
import { useHandleAfterNextDetails } from '../../utils';
import { useAreaDetails } from './functions';

function Details() {
  useElementBottomPosition(SNACKBAR_POSITION);
  const page = useSelector(pageDataSelector);
  const { useNextStepFormHandler } = usePageContext();
  const onAfterNext = useHandleAfterNextDetails();
  const handleNextStep = useNextStepFormHandler({ onAfterNext });
  const { validate, countries } = useAreaDetails();
  const showPopup = useFormFieldPopup();

  const memoPage = useMemo(() => {
    return {
      ...page,
      coords: getCoordsString(page.latitude, page.longitude),
    };
  }, [page]);

  return (
    <Form
      onSubmit={handleNextStep}
      initialValues={memoPage}
      validate={validate}
      mutators={{
        ...arrayMutators,
        generateHandleValue,
        countryIdMutator,
        coordsMutator,
        addressMutator,
      }}
      render={({
        handleSubmit,
        submitting,
        invalid,
        values,
        form: {
          mutators: { generateHandleValue, coordsMutator, addressMutator },
        },
        submitError,
      }) => (
        <form onSubmit={handleSubmit} autoComplete="nope">
          <UnsavedChangesFormWatcher />

          {submitting && <Loader isFillBlock />}
          <FormTitle center>{page.id ? 'Edit Area Page Content' : 'Create New Content'}</FormTitle>

          <FormTitle hint="Enter information to organize this content so that you can find it easily later.">
            I. Page Details
          </FormTitle>

          <LabelWrapper>
            <Label>Area Name*</Label>
            <Text accent>The name of a page will appear throughout the client facing itinerary.</Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="name"
            placeholder="Maasai Mara"
            type="text"
            onCustomChange={generateHandleValue}
            onInput={capitalizeAllWordsForInput}
          />

          <Field
            component={FormGroupAdapter}
            name="countryId"
            placeholder="Kenya"
            type="select"
            label="Country*"
            options={countries}
          />

          <LabelWrapper>
            <Label>Tags</Label>
            <Text accent>
              Tags allow you to create tailored versions of your content pages for different audiences and/or seasons.
              You can leave blank if this is the only version of this page.
            </Text>
          </LabelWrapper>
          <Field
            component={FormGroupAdapter}
            name="tags"
            placeholder="Migration"
            type="tags"
            onCustomChange={generateHandleValue}
            disabled={values?.tags?.length > 2}
            onFocus={showPopup(
              <Text accent style={{ fontSize: '1.5rem' }}>
                <b>Tool Tip:</b>
                <br />
                <br />
                Tags are an easy way to create multiple versions of your Content Page. <br />
                <br />
                <b>Examples:</b> <br />
                <br />
                Migration <br />
                <br />
                Nonmigration <br />
                <br />
                Green season <br />
                <br />* Please use sparingly. You are limited to three tags.
              </Text>,
            )}
          />

          <LabelWrapper>
            <Label>@Handle</Label>
            <Text accent>Automatically generated from combining the name and tag fields.</Text>
          </LabelWrapper>
          <Field component={FormGroupAdapter} name="handle" placeholder="@MaasaiMaraMigration" type="text" readOnly />

          <GoogleMaps
            step="II"
            lat={values.latitude}
            lng={values.longitude}
            onChangeCoords={coordsMutator}
            onChangeAddress={addressMutator}
          />

          {submitError && <FormErrorText>{submitError}</FormErrorText>}

          <StickyFooter>
            <FormActions isRight>
              <BuilderFooterButton htmlType="submit" disabled={submitting || invalid}>
                Next
              </BuilderFooterButton>
            </FormActions>
          </StickyFooter>

          <PageFormButtons />
        </form>
      )}
    />
  );
}

export default Details;
